import { Fragment, ReactNode, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router-dom";
import { Dialog, Menu, Transition, Disclosure } from "@headlessui/react";
import {
  CalendarIcon,
  CakeIcon,
  PresentationChartBarIcon,
  PresentationChartLineIcon,
  ClockIcon,
  DocumentCurrencyDollarIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  Bars3BottomLeftIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  UserGroupIcon,
  CpuChipIcon,
  DevicePhoneMobileIcon,
  MapPinIcon,
  BuildingStorefrontIcon,
  ShoppingCartIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  ShoppingBagIcon,
  LockClosedIcon,
  GiftTopIcon,
  BanknotesIcon,
  TicketIcon,
  NewspaperIcon, BriefcaseIcon, GiftIcon, MagnifyingGlassIcon, PhotoIcon, CalendarDaysIcon, LifebuoyIcon,
  LinkIcon,
  ArchiveBoxIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsRightLeftIcon,
  IdentificationIcon,
  ReceiptRefundIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import { UserIcon } from "@heroicons/react/20/solid";
import { OrganizationSelect } from "./inputs/OrganizationSelect";
import { EventSliderSelector } from "./inputs/EventSliderSelector";
import { useLogout, usePermissions } from "../modules/auth/api";
import {
  canViewOrderAheadOrders,
  canViewOrderAheadSetup,
} from "../modules/order-ahead/access";
import { processorAccessFor } from "../modules/payment_processor_config/access";
import { stripeConnectAccessFor } from "../modules/stripe_connect/access";
import { cashRefundAccessFor } from "../modules/cash_refunds/access";
import { useUserStore } from "../store/user";
import { capitalize, classNames } from "../utils";

type IconType = React.ForwardRefExoticComponent<
    React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> &
    React.RefAttributes<SVGSVGElement>
>;

interface NavItem {
  name: string;
  current: boolean;
  href?: string;
  icon?: IconType;
  children?: NavItem[];
  /** External URL — render a plain <a target="_blank"> instead of a router Link. */
  external?: boolean;
}

// "Dashboard Organizer" is a custom role (no fixed role_id), so we match on
// role.name (internal slug) and role.display_name (the human-readable label
// shown in the role dropdown), normalized to ignore case, whitespace,
// underscores, and hyphens.
const isDashboardOrganizer = (user?: any) => {
  const norm = (s: any) =>
    (s ? String(s) : "").toLowerCase().replace(/[\s_-]/g, "");
  return (
    norm(user?.role?.name).includes("dashboardorganizer") ||
    norm(user?.role?.display_name).includes("dashboardorganizer")
  );
};

const buildNavigation = (permissions: any, path: string, user?: any) => {
  const pointOfSaleReports = [
    {
      // Parallel new-reporting-engine pages for old-vs-new QA comparison. RONIN-INTERNAL ONLY
      // while in QA — gated EXACTLY like the Reconciliation tool below (user.role_id !== 1), so
      // customers and customer-admins never see it. Route access is guarded by requireRoninAdmin().
      name: "🆕 New Reporting (Compare)",
      icon: CurrencyDollarIcon,
      href: "/reports-new",
      current: path.startsWith("/reports-new"),
      hidden: user?.role_id !== 1,
    },
    {
      name: "Financial Report",
      icon: CurrencyDollarIcon,
      href: "/reports/financial-report",
      current: path === "/reports/financial-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_FINANCIAL"
      )?.value,
    },
    {
      name: "Product Mix Report",
      icon: ClipboardDocumentListIcon,
      href: "/reports/product-mix-report",
      current: path === "/reports/product-mix-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_PROUCT_MIX"
      )?.value,
    },
    {
      name: "Product Mix (Updated)",
      icon: ClipboardDocumentListIcon,
      href: "/reports/product-mix-updated",
      current: path === "/reports/product-mix-updated",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Clerk Report",
      icon: UsersIcon,
      href: "/reports/clerk-report",
      current: path === "/reports/clerk-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_CLERK"
      )?.value,
    },
    {
      name: "Location Sales Report",
      icon: MapPinIcon,
      href: "/reports/location-report",
      current: path === "/reports/location-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_LOCATION"
      )?.value,
    },
    {
      name: "Vendor Sales Report",
      icon: ShoppingCartIcon,
      href: "/reports/vendor-report",
      current: path === "/reports/vendor-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_VENDOR"
      )?.value,
    },
    {
      name: "Vendor Payouts",
      icon: BanknotesIcon,
      href: "/reports/vendor-payouts",
      current: path === "/reports/vendor-payouts",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_VENDOR"
      )?.value,
    },
    {
      name: "Event Sales Report",
      icon: CakeIcon,
      href: "/reports/event-report",
      current: path === "/reports/event-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_EVENT"
      )?.value,
    },
    {
      name: "Sub-Event Sales Report",
      icon: CalendarDaysIcon,
      href: "/reports/sub-event-report",
      current: path === "/reports/sub-event-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_SUB_EVENT"
      )?.value,
    },
    {
      name: "Vendor Type Sales Report",
      icon: ShoppingCartIcon,
      href: "/reports/vendor-type-report",
      current: path === "/reports/vendor-type-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_VENDOR_TYPE"
      )?.value,
    },
    {
      name: "Product Summary Report",
      icon: ClipboardDocumentListIcon,
      href: "/reports/product-mix-summary-report",
      current: path === "/reports/product-mix-summary-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_PRODUCT_SUMMARY"
      )?.value,
    },
    {
      name: "Discounts Report",
      icon: CurrencyDollarIcon,
      href: "/reports/discounts-report",
      current: path === "/reports/discounts-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_DISCOUNTS"
      )?.value,
    },
    {
      name: "Promo Codes Report",
      icon: GiftIcon,
      href: "/reports/promo-codes-report",
      current: path === "/reports/promo-codes-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_PROMO_CODES"
      )?.value,
    },
    {
      name: "Net Hourly Report",
      icon: PresentationChartLineIcon,
      href: "/reports/net-hourly-report",
      current: path === "/reports/net-hourly-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_NET_HOURLY"
      )?.value,
    },
    {
      name: "Cumulative Hourly Report",
      icon: PresentationChartBarIcon,
      href: "/reports/cumulative-net-hourly-report",
      current: path === "/reports/cumulative-net-hourly-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_CUMULATIVE_HOURLY"
      )?.value,
    },
    {
      name: "Transaction Report",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/transaction-report",
      current: path === "/reports/transaction-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_TRANSACTION"
      )?.value,
    },
    {
      name: "Order Items Report",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/order-items-report",
      current: path === "/reports/order-items-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_ORDER_ITEMS"
      )?.value,
    },
    {
      name: "Cash Balance Report",
      icon: BanknotesIcon,
      href: "/reports/cash-balance-report",
      current: path === "/reports/cash-balance-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_CASH_BALANCE"
      )?.value,
    },
    {
      name: "Promo Balance Report",
      icon: GiftIcon,
      href: "/reports/promo-balance-report",
      current: path === "/reports/promo-balance-report",
      hidden: !permissions["reporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_PROMO_BALANCE"
      )?.value,
    },
    {
      name: "GL Account Report",
      icon: BriefcaseIcon,
      href: "/reports/gl-account-report",
      current: path === "/reports/gl-account-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_GL_ACCOUNT"
      )?.value,
    },
    {
      name: "Deferred Report",
      icon: ClockIcon,
      href: "/reports/deferred-transaction-report",
      current: path === "/reports/deferred-transaction-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_DEFERRED"
      )?.value,
    },
    {
      name: "Event Summary Report",
      icon: ChartBarIcon,
      href: "/reports/event-summary-report",
      current: path === "/reports/event-summary-report",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Vertical Sales Report",
      icon: ChartBarIcon,
      href: "/reports/vertical-sales-report",
      current: path === "/reports/vertical-sales-report",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Commissary Report",
      icon: ClipboardDocumentListIcon,
      href: "/reports/commissary-report",
      current: path === "/reports/commissary-report",
      hidden: !permissions["reporting"]?.length,
    },
  ];

  const roninInternalTools = [
    {
      name: "Admin App Usage",
      icon: ChartBarIcon,
      href: "/reports/admin-app-usage",
      current: path === "/reports/admin-app-usage",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Reconciliation",
      icon: MagnifyingGlassIcon,
      href: "/reports/mx-merchant-reconciliation",
      current: path === "/reports/mx-merchant-reconciliation",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Event Summary",
      icon: NewspaperIcon,
      current: path.includes("/event-summary"),
      href: "/event-summary",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Merge Vendor",
      icon: ArrowsRightLeftIcon,
      current: path === "/merge-vendor",
      href: "/merge-vendor",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Refund Manager",
      icon: ReceiptRefundIcon,
      current: path === "/refund-manager",
      href: "/refund-manager",
      hidden: user?.role_id !== 1,
    },
    {
      name: "UID Manager",
      icon: IdentificationIcon,
      current: path === "/uid-manager",
      href: "/uid-manager",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Vendor Insights",
      icon: PresentationChartLineIcon,
      current: path === "/vendor-insights",
      href: "/vendor-insights",
      hidden: user?.role_id !== 1,
    },
    // External Ronin tools — open in a new tab (external: true renders a
    // plain <a target="_blank"> instead of a router Link, which would try to
    // client-navigate and mangle the absolute URL).
    {
      name: "Commissary Manager",
      icon: BuildingStorefrontIcon,
      current: false,
      href: "https://webfrontend.ashyfield-84b5f8cf.centralus.azurecontainerapps.io/",
      external: true,
      hidden: user?.role_id !== 1,
    },
    {
      name: "Inventory Manager",
      icon: ArchiveBoxIcon,
      current: false,
      href: "https://inventory-manager.gentlesky-d449526e.centralus.azurecontainerapps.io/login",
      external: true,
      hidden: user?.role_id !== 1,
    },
    {
      name: "Vendor Manager",
      icon: BriefcaseIcon,
      current: false,
      href: "https://vendor-manager.roninpos.com/",
      external: true,
      hidden: user?.role_id !== 1,
    },
  ];


  const ticketingReports = [
    {
      // NEW-ENGINE Ticketing Financial (RP-2174) — a SECOND link to the same /reports-new page,
      // placed here because ops look for ticketing reports under this tab. The entry on the
      // New Reporting landing stays; this is an additional pointer, not a move. Gated exactly
      // like the 🆕 New Reporting section above (Ronin staff only) — the /reports-new route
      // guard (requireRoninAdmin) would bounce anyone else anyway, so the link and the route
      // agree on who can see it.
      name: "🆕 Financial Report (New)",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports-new/ticketing-financial",
      current: path === "/reports-new/ticketing-financial",
      hidden: user?.role_id !== 1,
    },
    {
      name: "Financial Report",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/financial-report-ticketing",
      current: path === "/reports/financial-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_FINANCIAL"
      )?.value,
    },
    {
      name: "Net Hourly Report",
      icon: PresentationChartLineIcon,
      href: "/reports/net-hourly-report-ticketing",
      current: path === "/reports/net-hourly-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_NET_HOURLY"
      )?.value,
    },
    {
      name: "Cumulative Hourly Report",
      icon: PresentationChartBarIcon,
      href: "/reports/cumulative-net-hourly-report-ticketing",
      current: path === "/reports/cumulative-net-hourly-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_CUMULATIVE_HOURLY"
      )?.value,
    },
    {
      name: "Transaction Report",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/transaction-report-ticketing",
      current: path === "/reports/transaction-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_TRANSACTION"
      )?.value,
    },
    {
      name: "Event Sales Report",
      icon: CakeIcon,
      href: "/reports/event-report-ticketing",
      current: path === "/reports/event-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_EVENT"
      )?.value,
    },
    {
      name: "Item Report",
      icon: ClipboardDocumentListIcon,
      href: "/reports/item-report-ticketing",
      current: path === "/reports/item-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_PRODUCT_MIX"
      )?.value,
    },
    {
      name: "Promo Codes Report",
      icon: GiftIcon,
      href: "/reports/promo-codes-report-ticketing",
      current: path === "/reports/promo-codes-report-ticketing",
      hidden: !permissions["ticketingReporting"]?.find(
        (report: { label: string; value: string }) =>
          report?.value === "REPORTS_ACCESS_PROMO_CODES"
      )?.value,
    },
  ];

  const accessReports = [
    {
      name: "Access Scans Report",
      href: "/reports/access-scans-report",
      icon: PresentationChartLineIcon,
      current: path === "/reports/access-scans-report",
      hidden: !permissions["accessReporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_ACCESS_SCANS"
      )?.value,
    },
  ];
  const webCheckoutReports = [
    {
      name: "Web Transactions Report",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/web-checkout-report",
      current: path === "/reports/web-checkout-report",
      hidden: !permissions["webCheckoutReporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_WEB_CHECKOUT"
      )?.value,
    },
  ];

  const ordersReports = [
    {
      name: "Order Search",
      icon: MagnifyingGlassIcon,
      current: path.includes("/orders"),
      href: "/orders/list",
      hidden: !permissions["orders"]?.length,
    },
    {
      name: "Transaction Report",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/transaction-report",
      current: path === "/reports/transaction-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_TRANSACTION"
      )?.value,
    },
    {
      name: "Tab Management",
      icon: DocumentCurrencyDollarIcon,
      href: "/reports/tab-management-report",
      current: path === "/reports/tab-management-report",
      hidden: !permissions["reporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "REPORTS_ACCESS_TAB_MANAGEMENT"
      )?.value,
    },
    {
      name: "Commissary Orders",
      icon: ClipboardDocumentListIcon,
      href: "/commissary-orders/list",
      current: path.includes("/commissary-orders"),
      hidden: user?.role_id !== 1,
    },
  ];

  const orderAhead = [
    {
      name: "Event Setup",
      icon: MapPinIcon,
      current: path.includes("/order-ahead/overview"),
      href: "/order-ahead/overview",
      // Shared with the route guard — see modules/order-ahead/access.ts.
      hidden: !canViewOrderAheadSetup(user),
    },
    {
      name: "Order Ahead Orders",
      icon: MagnifyingGlassIcon,
      current: path.includes("/order-ahead-orders"),
      href: "/order-ahead-orders/list",
      // Shared with the route guard — see modules/order-ahead/access.ts.
      hidden: !canViewOrderAheadOrders(user),
    },
  ];

  // Timeclock pages are visible to admins, any "Dashboard Organizer" role, and
  // any role granted the matching permission under Roles → Timeclock.
  const canSeeTimeclock = (permissionValue: string) =>
    user?.role_id === 1 ||
    isDashboardOrganizer(user) ||
    Boolean(
      permissions["timeclock"]?.find(
        (p: { label: string; value: string }) => p?.value === permissionValue
      )
    );

  const timeclockChildren = [
    {
      name: "Timeclock Manager",
      icon: ClockIcon,
      current: path.includes("/timeclock-manager"),
      href: "/timeclock-manager/list",
      hidden: !canSeeTimeclock("TIMECLOCK_MANAGER_ACCESS"),
    },
    {
      name: "Timeclock Report",
      icon: PresentationChartBarIcon,
      current: path.includes("/timeclock-report"),
      href: "/timeclock-report/view",
      hidden: !canSeeTimeclock("TIMECLOCK_REPORT_ACCESS"),
    },
  ].filter(({ hidden }) => !hidden);

  const ticketingReportsChildren = ticketingReports.filter(
    ({ hidden }) => !hidden
  );
  const pointOfSaleReportsChildren = pointOfSaleReports.filter(
    ({ hidden }) => !hidden
  );
  const roninInternalToolsChildren = roninInternalTools.filter(
    ({ hidden }) => !hidden
  );
  const accessReportsChildren = accessReports.filter(
      ({ hidden }) => !hidden
  );
  const webCheckoutReportsChildren = webCheckoutReports.filter(
    ({ hidden }) => !hidden
  );
  const ordersChildren = ordersReports.filter(
      ({ hidden }) => !hidden
  );
  const orderAheadChildren = orderAhead.filter(({ hidden }) => !hidden);
  const ticketing = [
    {
      name: "Ticketing Setup (new)",
      icon: RectangleStackIcon,
      current: path.includes("/ticketing-setup"),
      href: "/ticketing-setup",
      hidden: !permissions["ticketingReporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "TICKET_ITEMS"
      )?.value,
    },
    {
      name: "Ticketing Items",
      icon: ShoppingCartIcon,
      current: path.includes("/ticketing-items"),
      href: "/ticketing-items/list",
      hidden: !permissions["ticketingReporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "TICKET_ITEMS"
      )?.value,
    },
    {
      name: "Ticket Search",
      icon: MagnifyingGlassIcon,
      current: path.includes("/tickets/list"),
      href: "/tickets/list",
      hidden: !permissions["ticketingReporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "TICKET_SEARCH"
      )?.value,
    },
    {
      name: "Ticket Comp",
      icon: CurrencyDollarIcon,
      current: path.includes("/tickets/ticket-comp"),
      href: "/tickets/ticket-comp",
      hidden: !permissions["ticketingReporting"]?.find(
          (report: { label: string; value: string }) =>
              report?.value === "TICKET_COMP"
      )?.value,
    },
  ];
  return [
    {
      name: "Dashboard",
      icon: HomeIcon,
      current: path === "/",
      href: "/",
      hidden: false,
    },
    {
      name: "Orders",
      icon: ShoppingBagIcon,
      current: path.includes("/orders"),
      children: ordersChildren,
      hidden: !permissions["orders"]?.length,
    },
    {
      name: "Order Ahead",
      icon: ClipboardDocumentListIcon,
      current: path.includes("/order-ahead"),
      children: orderAheadChildren,
      hidden: orderAheadChildren.length === 0,
    },
    {
      name: "Reporting Glossary",
      icon: NewspaperIcon,
      href: "/reporting-glossary",
      current: path.includes("/reporting-glossary"),
    },
    {
      name: "Point of Sale Reports",
      icon: ChartBarIcon,
      current: false,
      children: pointOfSaleReportsChildren,
      hidden: !permissions["reporting"]?.length,
    },
    {
      name: "Organizations",
      icon: FolderIcon,
      current: path.includes("/organizations"),
      href: "/organizations/list",
      hidden: !permissions["organizations"]?.length,
    },
    {
      name: "Payment Processors",
      icon: CreditCardIcon,
      current: path.includes("/payment-processors"),
      href: "/payment-processors/list",
      // Visible to: admins, anyone with an explicit payment_processors
      // permission, and full Dashboard Organizers (view-only, legacy). Rules
      // live in payment_processor_config/access.ts so the routes enforce
      // exactly what this link advertises.
      hidden: !processorAccessFor(user).view,
    },
    {
      name: "Stripe Connect",
      icon: LinkIcon,
      current: path.includes("/stripe-connect"),
      href: "/stripe-connect/list",
      // Visibility is driven by the `stripe_connect` role permission (utils.ts
      // modules). Admins always see it; grant "Stripe Connect → Access" to any
      // role in the Role editor to expose the page for that role. Rules live in
      // stripe_connect/access.ts so the route enforces exactly what this link
      // advertises.
      hidden: !stripeConnectAccessFor(user).access,
    },
    {
      name: "Cash Refunds",
      icon: BanknotesIcon,
      current: path.includes("/cash-refunds"),
      href: "/cash-refunds/list",
      // Same pattern as Stripe Connect: the `cash_refunds` role permission
      // drives it, admins always see it, and the rule lives in
      // cash_refunds/access.ts so the route enforces what this link advertises.
      hidden: !cashRefundAccessFor(user).access,
    },
    {
      name: "Events",
      icon: CalendarIcon,
      current: path.includes("/events"),
      href: "/events/list",
      hidden: !permissions["events"]?.length,
    },
    {
      name: "Vendors",
      icon: BuildingStorefrontIcon,
      current: path.includes("/vendors"),
      href: "/vendors/list",
      hidden: !permissions["vendors"]?.length,
    },
    {
      name: "Locations",
      icon: MapPinIcon,
      current: path.includes("/locations"),
      href: "/locations/list",
      hidden: !permissions["locations"]?.length,
    },
    {
      name: "Menus",
      icon: DocumentTextIcon,
      current: path.includes("/menus"),
      href: "/menus/list",
      hidden: !permissions["menus"]?.length,
    },
    {
      name: "Items",
      icon: ShoppingCartIcon,
      current: path.includes("/items"),
      href: "/items/list",
      hidden: !permissions["items"]?.length,
    },
    {
      name: "Item Modifiers",
      icon: PencilSquareIcon,
      current: path.includes("/item-modifiers"),
      href: "/item-modifiers/list",
      hidden: !permissions["items_modifiers"]?.length,
    },
    {
      name: "Discounts",
      icon: CurrencyDollarIcon,
      current: path.includes("/discounts"),
      href: "/discounts/list",
      hidden: !permissions["discounts"]?.length,
    },
    {
      name: "Promo Code",
      icon: GiftTopIcon,
      current: path.includes("/promocode"),
      href: "/promocode/list",
      hidden: !permissions["promocode"]?.length,
    },
    {
      name: "Users",
      icon: UsersIcon,
      current: path.includes("/users"),
      href: "/users/list",
      hidden: !permissions["users"]?.length,
    },
    {
      name: "Roles",
      icon: LockClosedIcon,
      current: path.includes("/roles"),
      href: "/roles/list",
      hidden: !permissions["roles"]?.length,
    },
    {
      name: "Attendees",
      icon: UserGroupIcon,
      current: path.includes("/attendees") || path.includes("/reports/attendee-demographics-report"),
      children: [
        {
          name: "Manage Attendees",
          icon: UserGroupIcon,
          current: path.includes("/attendees"),
          href: "/attendees/list",
          hidden: !permissions["attendees"]?.length,
        },
        {
          name: "Demographics",
          icon: PresentationChartBarIcon,
          current: path.includes("/reports/attendee-demographics-report"),
          href: "/reports/attendee-demographics-report",
          // Visible to admins (role_id 1) and any "Dashboard Organizer" role.
          // We check BOTH role.name (internal slug) and role.display_name
          // (the human-readable label shown in the role dropdown), normalized
          // to ignore whitespace, underscores, hyphens, and case. The display
          // name is what shows as "Dashboard Organizer" in the user form, so
          // matching on it is the most reliable signal.
          hidden: (() => {
            if (user?.role_id === 1) return false;
            const norm = (s: any) =>
              (s ? String(s) : "")
                .toLowerCase()
                .replace(/[\s_-]/g, "");
            const roleName = norm(user?.role?.name);
            const roleDisplay = norm(user?.role?.display_name);
            const isDashOrg =
              roleName.includes("dashboardorganizer") ||
              roleDisplay.includes("dashboardorganizer");
            // Temporary debug: log once per render so we can see what the
            // current user's role actually looks like. Remove after the gate
            // is confirmed working.
            if (typeof window !== "undefined" && !(window as any).__demoDbg) {
              (window as any).__demoDbg = true;
              // eslint-disable-next-line no-console
              console.log("[Demographics gate]", {
                role_id: user?.role_id,
                role_name: user?.role?.name,
                role_display_name: user?.role?.display_name,
                normalizedName: roleName,
                normalizedDisplay: roleDisplay,
                isDashOrg,
              });
            }
            return !isDashOrg;
          })(),
        },
      ].filter(({ hidden }) => !hidden),
      hidden: !permissions["attendees"]?.length,
    },
    {
      name: "Cashless Assets",
      icon: CpuChipIcon,
      current: path.includes("/rfid_assets"),
      href: "/rfid-assets/list",
      hidden: !permissions["cashless_assets"]?.length,
    },
    {
      name: "Devices",
      icon: DevicePhoneMobileIcon,
      current: path.includes("/devices"),
      href: "/devices/list",
      hidden: !permissions["devices"]?.length,
    },
    {
      name: "Image Library",
      icon: PhotoIcon,
      current: path.includes("/image-library"),
      href: "/image-library/list",
      hidden: !permissions["image_library"]?.length,
    },
    {
      name: "Invoices",
      icon: BanknotesIcon,
      current: path.includes("/invoices"),
      href: "/invoices/list",
      hidden: !permissions["invoices"]?.length,
    },
    {
      name: "Invoice Items",
      icon: RectangleStackIcon,
      current: path.includes("/invoice-items"),
      href: "/invoice-items/list",
      hidden: !permissions["invoices"]?.length,
    },
    {
      name: "Timeclock",
      icon: ClockIcon,
      current: path.includes("/timeclock-manager") || path.includes("/timeclock-report"),
      // Visible to admins (role_id 1), any "Dashboard Organizer" role, and any
      // role granted the matching Timeclock permission in the Role editor.
      children: timeclockChildren,
      hidden: timeclockChildren.length === 0,
    },
    {
      name: "Ticketing",
      icon: TicketIcon,
      current: false,
      children: ticketing,
      hidden: !permissions["ticketingReporting"]?.length,
    },
    {
      name: "Ticketing Reports",
      icon: ChartBarIcon,
      current: false,
      children: ticketingReportsChildren,
      hidden: !permissions["ticketingReporting"]?.length,
    },
    {
      name: "Access Control Reports",
      icon: ChartBarIcon,
      current: false,
      children: accessReportsChildren,
      hidden: !permissions["accessReporting"]?.length,
    },
    {
      name: "Web Checkout Reports",
      icon: ChartBarIcon,
      current: false,
      children: webCheckoutReportsChildren,
      hidden: !permissions["webCheckoutReporting"]?.length,
    },
    {
      name: "Ronin Internal Tools",
      icon: CpuChipIcon,
      current: false,
      children: roninInternalToolsChildren,
      hidden: user?.role_id !== 1 || roninInternalToolsChildren.length === 0,
    },
  ].filter(({ hidden }) => !hidden);
};

export default function Sidebar({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const error = useRouteError();
  const user = useUserStore((state) => state.user);
  const [logout] = useLogout();
  const location = useLocation();
  const { permissions } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigation: NavItem[] = buildNavigation(permissions ?? "", location.pathname, user);
  const { moduleWisePermission } = usePermissions("dropdown");

  const eventPermission = moduleWisePermission?.find(
      (p: { label: string; value: string }) => p.value === "EVENT_DROPDOWN_ACCESS"
  );

  const organizationPermission = moduleWisePermission?.find(
      (p: { label: string; value: string }) => p.value === "ORGANIZATION_DROPDOWN_ACCESS"
  );

  return (
      <div className="min-h-screen">
        {/* Mobile sidebar */}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="relative z-40 md:hidden" onClose={setSidebarOpen}>
            <Transition.Child
                as={Fragment}
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                  as={Fragment}
                  enter="transition ease-in-out duration-300 transform"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transition ease-in-out duration-300 transform"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-primary-700 pt-5 pb-4">
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                        type="button"
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                    >
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5 pb-4">
                    <div className="flex items-center px-4">
                      <img className="h-8 w-auto" src="/static/logo_color_no_bg.png" alt="Ronin" />
                    </div>
                    <div className="mt-5 flex flex-grow flex-col">
                      <nav className="flex-1 space-y-1 px-2" aria-label="Sidebar">
                        {navigation.map((item) =>
                            item.children ? (
                                <Disclosure
                                    as="div"
                                    key={item.name}
                                    defaultOpen={location.pathname.includes("/reports")}
                                    className="space-y-1"
                                >
                                  {({ open }) => (
                                      <>
                                        <Disclosure.Button
                                            className={classNames(
                                                item.current ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                                "group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            )}
                                        >
                                          {item.icon && (
                                              <item.icon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                          )}
                                          <span className="flex-1">{item.name}</span>
                                          <svg
                                              className={classNames(
                                                  open ? "text-gray-400 rotate-90" : "text-gray-300",
                                                  "ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400"
                                              )}
                                              viewBox="0 0 20 20"
                                              aria-hidden="true"
                                          >
                                            <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                                          </svg>
                                        </Disclosure.Button>
                                        <Disclosure.Panel className="space-y-1 bg-gray-100 rounded-lg">
                                          {item.children?.map((subItem) => (
                                              subItem.external ? (
                                                  <a
                                                      key={subItem.name}
                                                      href={subItem.href}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      onClick={() => setSidebarOpen(false)}
                                                      className="group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                                                  >
                                                    {subItem.icon && (
                                                        <subItem.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                                    )}
                                                    {subItem.name}
                                                    <ArrowTopRightOnSquareIcon className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400" />
                                                  </a>
                                              ) : (
                                              <Link
                                                  key={subItem.name}
                                                  to={subItem.href ?? "#"}
                                                  onClick={() => setSidebarOpen(false)}
                                                  className={classNames(
                                                      subItem.current ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                                      "group flex w-full items-center rounded-md py-2 pl-11 pr-2 text-sm font-medium"
                                                  )}
                                              >
                                                {subItem.icon && (
                                                    <subItem.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                                )}
                                                {subItem.name}
                                              </Link>
                                              )
                                          ))}
                                        </Disclosure.Panel>
                                      </>
                                  )}
                                </Disclosure>
                            ) : (
                                <Link
                                    key={item.name}
                                    to={item.href ?? "#"}
                                    onClick={() => setSidebarOpen(false)}
                                    className={classNames(
                                        item.current ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                        "group flex items-center pl-2 py-2 text-sm font-medium rounded-md"
                                    )}
                                >
                                  {item.icon && (
                                      <item.icon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                  )}
                                  {item.name}
                                </Link>
                            )
                        )}
                      </nav>
                      {user?.role_id === 1 && (
                        <div className="px-2 pb-4">
                          <a
                            href="https://ronin-support.azurewebsites.net/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center pl-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                          >
                            <LifebuoyIcon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                            Support
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true" />
            </div>
          </Dialog>
        </Transition.Root>

        {/* Desktop sidebar */}
        <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
          <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5 pb-4">
            <div className="flex items-center justify-center px-4">
              <img className="h-40 w-auto justify-self-center" src="/static/logo-standard-compressed.svg" alt="Ronin" />
            </div>
            <div className="mt-5 flex flex-grow flex-col">
              <nav className="flex-1 space-y-1 px-2" aria-label="Sidebar">
                {navigation.map((item) =>
                    item.children ? (
                        <Disclosure key={item.name} as="div" className="space-y-1">
                          {({ open }) => (
                              <>
                                <Disclosure.Button
                                    className={classNames(
                                        item.current ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                        "group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    )}
                                >
                                  {item.icon && (
                                      <item.icon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                  )}
                                  <span className="flex-1">{item.name}</span>
                                  <svg
                                      className={classNames(
                                          open ? "text-gray-400 rotate-90" : "text-gray-300",
                                          "ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400"
                                      )}
                                      viewBox="0 0 20 20"
                                      aria-hidden="true"
                                  >
                                    <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                                  </svg>
                                </Disclosure.Button>
                                <Disclosure.Panel className="space-y-1 bg-gray-100 rounded-lg">
                                  {item.children?.map((subItem) => (
                                      subItem.external ? (
                                          <a
                                              key={subItem.name}
                                              href={subItem.href}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="group flex w-full items-center rounded-md py-2 pl-8 pr-2 text-sm font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                                          >
                                            {subItem.icon && (
                                                <subItem.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                            )}
                                            {subItem.name}
                                            <ArrowTopRightOnSquareIcon className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400" />
                                          </a>
                                      ) : (
                                      <Link
                                          key={subItem.name}
                                          to={subItem.href ?? "#"}
                                          className={classNames(
                                              subItem.current ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                              "group flex w-full items-center rounded-md py-2 pl-8 pr-2 text-sm font-medium"
                                          )}
                                      >
                                        {subItem.icon && (
                                            <subItem.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                                        )}
                                        {subItem.name}
                                      </Link>
                                      )
                                  ))}
                                </Disclosure.Panel>
                              </>
                          )}
                        </Disclosure>
                    ) : (
                        <Link
                            key={item.name}
                            to={item.href ?? "#"}
                            className={classNames(
                                item.current ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-200 hover:text-gray-900",
                                "group flex items-center pl-2 py-2 text-sm font-medium rounded-md"
                            )}
                        >
                          {item.icon && (
                              <item.icon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                          )}
                          {item.name}
                        </Link>
                    )
                )}
              </nav>
              {user?.role_id === 1 && (
                <div className="px-2 pb-4">
                  <a
                    href="https://ronin-support.azurewebsites.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center pl-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                  >
                    <LifebuoyIcon className="mr-3 h-6 w-6 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
                    Support
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="flex flex-1 flex-col md:pl-64 min-h-screen">
          <div className="sticky top-0 z-10 flex bg-white shadow py-2">
            <button
                type="button"
                className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
                onClick={() => setSidebarOpen(true)}
            >
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex flex-1 flex-wrap pl-2 pr-9">
              {organizationPermission && (
                  <div className="ml-4 flex items-center grow md:ml-1 w-100">
                    <OrganizationSelect />
                  </div>
              )}
              {eventPermission && (
                  <div className="ml-4 flex items-center grow md:ml-1 w-100">
                    <EventSliderSelector />
                  </div>
              )}
              <div className="ml-4 flex justify-end items-center md:ml-1 w-100">
                <Menu as="div" className="relative ml-3">
                  <div>
                    <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                      {capitalize(user?.username)}
                      <UserIcon className="h-8 w-8 rounded-full" />
                    </Menu.Button>
                  </div>
                  <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Menu.Item>
                        <div
                            className="block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                            onClick={() => navigate("/profile")}
                        >
                          Profile
                        </div>
                      </Menu.Item>
                      <Menu.Item>
                        <div
                            className="block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                            onClick={logout}
                        >
                          Sign Out
                        </div>
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          <main className="h-[calc(100vh_-_64px)]">{children}</main>
        </div>
      </div>
  );
}
